import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store } from '@ngxs/store';
import { SortAllUsers } from 'app/library/all-users/actions';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { IChoosenFilterInterface } from 'app/library/all-users/interfaces/choosen-filter.interface';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { AllUsersApiService } from 'app/library/all-users/services/all-users.api-service';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-certificates-users-filter',
    templateUrl: './certificates-users-filter.component.html',
    styleUrls: ['./certificates-users-filter.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
})
export class CertificatesUsersFilterComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    users$: Observable<User[]>;

    @Output()
    addedToList = new EventEmitter<IChoosenFilterInterface[]>();

    dataSource: AllUserDataSource;
    displayedColumns = [];
    searchInput = new FormControl();
    hideEmail: boolean;
    list: IChoosenFilterInterface[] = [];
    hasActiveSearch = false;
    hasUsers = false;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    sortBy = this._store.selectSnapshot(AllUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(AllUsersState.getSortOrder);

    private _updateDataSource(): void {
        this.dataSource = new AllUserDataSource(
            this.users$.pipe(
                map((users) => {
                    return users.map((user) => {
                        user = Object.assign(user, user, {
                            alreadyMember: this.list.some((item) => item.entity.id === user.id),
                        });

                        return user;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    constructor(
        private _allUsersService: AllUsersApiService,
        private _translateService: TranslateService,
        private _featureService: FeatureService,
        private _store: Store,
        private _action$: Actions
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    ngOnInit(): void {
        this.displayedColumns = this.hideEmail ? ['avatar', 'firstName', 'lastName', 'buttons'] : ['avatar', 'firstName', 'lastName', 'email', 'buttons'];

        this.users$.pipe(takeUntil(this._unsubscribeAll)).subscribe((users) => {
            this.hasUsers = !!users?.length;
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.hasActiveSearch = typeof searchText === 'string' && !!searchText.trim();
            this.loadUsersPage(true);
        });

        this._allUsersService.filtersList$.pipe(takeUntil(this._unsubscribeAll)).subscribe((filters) => {
            this.list = filters;
            this._updateDataSource();
        });

        setTimeout(() => {
            if (this.sortBy && this.sortOrder) {
                this.sort.sort({
                    id: this.sortBy || '',
                    start: this.sortOrder || 'asc',
                    disableClear: false,
                });
            }

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });
        });
    }

    addToList(user: User): void {
        this.addedToList.emit([
            ...this.list,
            {
                entity: { id: user.id, type: 'Users' },
                valueToShow: `${this._translateService.instant('USER.PLURAL')}: ${user.fullName}`,
            },
        ]);
    }

    removeFromList(user: User): void {
        this.addedToList.emit(this.list.filter((item) => !(item.entity.id === user.id && item.entity.type === 'Users')));
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (!this.dataSource || !this.sort || !this.paginator) {
            return;
        }

        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadUsers(
            'certificateOwners',
            [],
            '',
            this.searchInput.value,
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1
        );
    }
}
